import { LoadingButton } from "@mui/lab";
import { DatePicker, DateValidationError } from "@mui/x-date-pickers";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Slide,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { CloseIconCircle, CrossIcon, InfoIcon } from "../../assets/icons";
import useAllergens from "../../hooks/useAllergens";
import {
  AddressResponse,
  AreaBriefResponse,
  MembershipUsersResponse,
  PartnerUserDetailResponse,
  PartnerUserDetailResponseGenderEnum,
  RegisterUserByAdminRequest,
} from "../../openapi/client";
import { IDialogProps } from "../../openapi/interfaces";
import { setNotification } from "../../store/notificationSlice";
import { RadioBoxCustom } from "../RadioBox";
import Tagging from "../Tagging";
import Transition from "../Transition";
import useValidation from "../Validation";
import useAddressValidation from "../Validation/adress";
import Api from "../../openapi";
import useZones from "../../hooks/useZones";
import useMedicalConditions from "../../hooks/useMedicalConditions";
import useDrivers from "../../hooks/useDrivers";
import useAffiliates from "../../hooks/useAffiliates";
import useDietitians from "../../hooks/useDietitians";
import moment, { Moment } from "moment";
import { checkIf, defaultFormat, disableDayCheck, displayFormat } from "../../utils";
import useAreasBrief from "../../hooks/useAreaBrief";
import useGetUsersOfMembership from "../../hooks/useGetUsersOfMembership";

interface CustomState {
  [key: string]: any;
}

export default function UserCrud(props: IDialogProps) {
  const dummyUser = {
    fullName: "",
    gender: null,
    birthDate: null,
    height: null,
    weight: null,
    address: {
      detail: "",
      area: undefined,
      zone: undefined,
      street: undefined,
      building: undefined,
      flat: undefined,
    },
    allergens: [],
    medicalConditions: [],
    phoneNumber: props.membershipInfo?.phoneNumber || "",
    deliveryPhoneNumber: "",
    active: true,
    externalId: "",
    deliveryNote: "",
  };
  const dispatch = useDispatch();
  const [user, setUserEdit] = useState<any>(dummyUser);
  const { validation, setValidation } = useValidation(user);
  const { validation: addressValidation, setValidation: setAddressValidation } = useAddressValidation(user.address);
  const [loading, setLoading] = useState(false);
  const [allergens, setAllergens] = useState<Array<string>>([]);
  const [medicalConditions, setMedicalConditions] = useState<Array<string>>([]);
  const [formDirty, setFormDirty] = useState(false);

  const allergenList = useAllergens();
  const dietitianList = useDietitians();
  const driverList = useDrivers();
  const affiliateList = useAffiliates();
  const zones = useZones();
  const areas = useAreasBrief();
  const medicalConditionList = useMedicalConditions();
  const { checkMembershipInfo } = useGetUsersOfMembership();

  const [showMembershipInfo, setShowMembershipInfo] = useState(false);
  const [membershipInfo, setMembershipInfo] = useState<MembershipUsersResponse>();

  const [startDate, setStartDate] = useState<Moment | null>(user.birthDate ? moment(user.birthDate) : null);
  const [open, setOpen] = useState(false);
  const calendarRef = useRef<any>(null);
  const [dateSelectionError, setDateSelectionError] = useState<DateValidationError | null>(null);
  const errorMessage = useMemo(() => {
    switch (dateSelectionError) {
      case "shouldDisableDate": {
        return "Your age is required to be between 15 and 99";
      }
      case "invalidDate": {
        return "Your date is not valid";
      }
      default: {
        return "";
      }
    }
  }, [dateSelectionError]);

  const handleFieldChange = (field: string, value: any) => {
    setFormDirty(true);
    if (field === "address") {
      setUserEdit((state: any) => ({ ...state, address: { ...state.address, ...(value as AddressResponse) } }));
    } else {
      setUserEdit((state: any) => ({ ...state, [field]: value }));
    }
  };

  const handleChangeBirthdate = (newValue: Moment | null) => {
    setStartDate(newValue as Moment);
    handleFieldChange("birthDate", moment(newValue).format(defaultFormat));
  };

  const dietitianChange = (event: SelectChangeEvent) => handleFieldChange("dietitianId", event.target.value);
  const driverChange = (event: SelectChangeEvent) => handleFieldChange("address", { driverId: event.target.value });

  const resetForm = () => {
    setUserEdit(dummyUser);
    setValidation((state) => ({ ...state, show: false }));
    setAddressValidation((state) => ({ ...state, show: false }));
    setAllergens([]);
    setMedicalConditions([]);
    setShowMembershipInfo(false);
    setMembershipInfo(undefined);
  };

  const updateAllergens = (_: any, selection: string) => {
    setAllergens((state) => {
      let temp = [...state];
      if (!temp.includes(selection)) {
        temp.push(selection);
      }
      return temp;
    });
  };

  const deleteAllergen = (selection: string) => {
    setAllergens((state) => {
      let temp = [...state];
      const findIndex = temp.indexOf(selection);
      temp.splice(findIndex, 1);
      return temp;
    });
  };

  const updateMedicalConditions = (_: any, selection: string) => {
    setMedicalConditions((state) => {
      let temp = [...state];
      if (!temp.includes(selection)) {
        temp.push(selection);
      }
      return temp;
    });
  };

  const deleteMedicalCondition = (selection: string) => {
    setMedicalConditions((state) => {
      let temp = [...state];
      const findIndex = temp.indexOf(selection);
      temp.splice(findIndex, 1);
      return temp;
    });
  };

  const handlePanelClose = (...args: any) => {
    resetForm();
    props.onClose(args);
  };

  const saveChanges = async () => {
    if (validation.error || addressValidation.error) {
      setValidation((state) => ({ ...state, show: true }));
      setAddressValidation((state) => ({ ...state, show: true }));
      return;
    }

    const createUserDetail: any = {
      fullName: user.fullName,
      phoneNumber: user.phoneNumber,
      deliveryPhoneNumber: user.deliveryPhoneNumber.length !== 4 ? user.deliveryPhoneNumber : null,
      gender: user.gender,
      birthDate: checkIf(user.birthDate) ? moment(user.birthDate).format(defaultFormat) : null,
      height: user.height ? Number(user.height) : null,
      weight: user.weight ? Number(user.weight) : null,
      allergens,
      medicalConditions,
      externalId: user.externalId || "",
      address: user.address,
      active: user.active,
      deliveryNote: user.deliveryNote,
      dietitianId: user.dietitianId,
      affiliateId: user.affiliateId,
    };

    try {
      setLoading(true);

      // make a separation between the user and the membership
      // if membership info is shown, create a new user for the membership
      if (showMembershipInfo || props.membershipInfo?.id) {
        const membershipInfoId = props.membershipInfo?.id ? props.membershipInfo.id : membershipInfo?.id;
        await Api.Membership.createUserIntoMembershipByAdmin(String(membershipInfoId), createUserDetail);
      } else {
        await Api.User.createUserByAdmin(createUserDetail);
      }

      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: "User has been created successfully",
        })
      );
      resetForm();
      props.onConfirm(true);
      props.onClose({});
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setLoading(false);
    setFormDirty(false);
  };

  const getUsersOfMembership = async () => {
    try {
      const res = await checkMembershipInfo(String(user.phoneNumber));
      setShowMembershipInfo(true);
      setMembershipInfo(res as MembershipUsersResponse);
    } catch (error) {}
  };

  const getUsersOfMembershipDebounced = debounce(getUsersOfMembership, 500);

  useEffect(() => {
    // Don't do this when creating a user into a membership
    if (props.membershipInfo?.id) return;

    // Don't do this when user's channel is rejeem

    if (validation.phoneNumber?.error === false) {
      getUsersOfMembershipDebounced();
    } else {
      setShowMembershipInfo(false);
      setMembershipInfo(undefined);
    }
  }, [validation.phoneNumber?.error]);

  useEffect(() => {
    if (props.membershipInfo?.id) {
      setUserEdit((state: any) => ({ ...state, phoneNumber: props.membershipInfo?.phoneNumber }));
    }
  }, [props.membershipInfo?.id]);

  // console.log("validation", validation);
  // console.log("user", user);
  // console.log("props.membershipInfo", props.membershipInfo);
  return (
    <Fragment>
      <Dialog
        open={props.open}
        TransitionComponent={Transition}
        onClose={handlePanelClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "16px",
            boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
            padding: "24px",
            width: "820px",
            maxWidth: "820px",
          },
          "& .MuiDialogTitle-root": {
            padding: "0 16px 16px 0",
          },
          "& .MuiDialogContent-root,& .MuiDialogActions-root": {
            padding: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "text.dark",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "20px",
          }}
        >
          {props.title}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handlePanelClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.fontLight2",
          }}
        >
          <CrossIcon />
        </IconButton>
        <DialogContent sx={{ paddingRight: "10px !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
            <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Name / Surname
                  </Typography>
                  <Typography component="span" className="required-label">
                    *
                  </Typography>
                </Box>
                <TextField
                  className="form-field"
                  value={user.fullName}
                  error={validation.show && validation.fullName?.error}
                  helperText={
                    validation.show && validation.fullName?.error ? validation.fullName.helperText : undefined
                  }
                  onChange={(e) => handleFieldChange("fullName", e.target.value)}
                />
              </Box>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  External ID
                </Typography>
                <TextField
                  className="form-field"
                  value={user.externalId}
                  onChange={(e) => handleFieldChange("externalId", e.target.value)}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Telephone
                  </Typography>
                  <Typography component="span" className="required-label">
                    *
                  </Typography>
                </Box>
                <TextField
                  className="form-field"
                  value={user?.phoneNumber?.slice(4)}
                  onChange={(e) => handleFieldChange("phoneNumber", `+974${e.target.value}`)}
                  error={validation.show && validation.phoneNumber?.error}
                  helperText={
                    validation.show && validation.phoneNumber?.error ? validation.phoneNumber.helperText : undefined
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+974</InputAdornment>,
                  }}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  disabled={Boolean(props.membershipInfo?.id)}
                />
              </Box>
              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Delivery Phone
                  </Typography>
                </Box>
                <TextField
                  className="form-field"
                  value={user?.deliveryPhoneNumber?.slice(4)}
                  onChange={(e) => handleFieldChange("deliveryPhoneNumber", `+974${e.target.value}`)}
                  error={validation.show && validation.deliveryPhoneNumber?.error}
                  helperText={
                    validation.show && validation.deliveryPhoneNumber?.error
                      ? validation.deliveryPhoneNumber.helperText
                      : undefined
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+974</InputAdornment>,
                  }}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                />
              </Box>
            </Box>
            {showMembershipInfo && !props.membershipInfo?.id ? (
              <Box
                className="form-section"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  columnGap: "4px",
                  border: "1px solid var(--blue)",
                  padding: "8px",
                  borderRadius: "8px",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", columnGap: "4px" }}>
                  <Typography
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center", color: "text.blue" }}
                  >
                    <InfoIcon />
                  </Typography>
                  <Typography sx={{ color: "text.blue", fontSize: "12px", fontWeight: 400 }}>
                    This phone number has a membership in use and a new user will be created for this membership.
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "row", gap: "4px" }}>
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "4px" }}>
                    <Typography sx={{ color: "text.blue", fontSize: "12px", fontWeight: 400 }}>
                      Registered User(s):
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "row", gap: "4px", flexWrap: "wrap", alignItems: "center" }}
                  >
                    {membershipInfo?.users?.map((user, index) => (
                      <Typography sx={{ color: "text.blue", fontSize: "12px", fontWeight: 600 }}>
                        {user.fullName} {index !== (membershipInfo?.users?.length || 0) - 1 ? "," : ""}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
            ) : null}
            <Box className="form-section">
              <Typography component="span" className="form-label">
                Gender
              </Typography>
              <RadioGroup
                className="form-field"
                sx={{ display: "flex", flexDirection: "row" }}
                value={user?.gender || null}
                onChange={(e) => handleFieldChange("gender", e.target.value)}
              >
                <FormControlLabel
                  className={user?.gender === PartnerUserDetailResponseGenderEnum.Male ? "selected" : ""}
                  value={PartnerUserDetailResponseGenderEnum.Male}
                  control={<RadioBoxCustom />}
                  label="Male"
                />
                <FormControlLabel
                  className={user?.gender === PartnerUserDetailResponseGenderEnum.Female ? "selected" : ""}
                  value={PartnerUserDetailResponseGenderEnum.Female}
                  control={<RadioBoxCustom />}
                  label="Female"
                />
              </RadioGroup>
              {validation.show && validation.gender?.error ? (
                <FormHelperText sx={{ color: "text.error" }}>{validation.gender.helperText}</FormHelperText>
              ) : null}
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", columnGap: "12px" }}>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Height
                </Typography>
                <TextField
                  className="form-field"
                  value={user?.height}
                  error={validation.show && validation.height?.error}
                  helperText={validation.show && validation.height?.error ? validation.height.helperText : undefined}
                  onChange={(e) => handleFieldChange("height", e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                  }}
                />
              </Box>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Weight
                </Typography>
                <TextField
                  className="form-field"
                  value={user?.weight}
                  error={validation.show && validation.weight?.error}
                  helperText={validation.show && validation.weight?.error ? validation.weight.helperText : undefined}
                  onChange={(e) => handleFieldChange("weight", e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", columnGap: "12px" }}>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Date of Birth
                </Typography>
                <DatePicker
                  sx={{
                    // "& > .MuiFormHelperText-root": { marginTop: "2px" },
                    "& .MuiFormHelperText-root": {
                      bottom: "-20px",
                      // position: "absolute",
                      left: 0,
                      right: 0,
                      // textAlign: "center",
                      color: "text.error",
                    },
                  }}
                  value={startDate}
                  open={open}
                  format={displayFormat}
                  onChange={(newValue: Moment | null) => handleChangeBirthdate(newValue as Moment)}
                  onAccept={() => setOpen(false)}
                  onClose={() => setOpen(false)}
                  onError={(newError: DateValidationError) => setDateSelectionError(newError)}
                  shouldDisableDate={(day: Moment) => disableDayCheck(day)}
                  ref={calendarRef}
                  slotProps={{
                    textField: {
                      onClick: () => setOpen(true),
                      helperText:
                        validation.show && validation.birthDate?.error
                          ? validation.birthDate.helperText
                          : errorMessage || null,
                    },
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Dietitian
                </Typography>
                <Select value={user?.dietitianId || null} onChange={dietitianChange} sx={{ marginBottom: 0 }}>
                  {dietitianList?.map((dietitianItem) => (
                    <MenuItem key={dietitianItem.id} value={dietitianItem.id}>
                      {dietitianItem.fullName}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              {affiliateList && affiliateList.length > 0 ? (
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Affiliate
                  </Typography>
                  <Select
                    value={user?.affiliateId || null}
                    onChange={(e) => handleFieldChange("affiliateId", e.target.value)}
                    sx={{ marginBottom: 0 }}
                  >
                    {affiliateList?.map((affiliateItem) => (
                      <MenuItem key={affiliateItem.id} value={affiliateItem.id}>
                        {affiliateItem.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              ) : null}
            </Box>
            <Box className="form-section">
              <Typography className="form-label-title">Address</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", columnGap: "12px" }}>
              <Box sx={{ display: "flex", gap: "12px", flex: "1 1" }}>
                <Box className="form-section" sx={{ flex: 2 }}>
                  <Box sx={{ display: "flex" }}>
                    <Typography component="span" className="form-label">
                      Zone
                    </Typography>
                    <Typography component="span" className="required-label">
                      *
                    </Typography>
                  </Box>

                  <Autocomplete
                    className="form-field"
                    options={zones}
                    getOptionLabel={(zone) => zone.toString()}
                    onChange={(event: any, newValue: number | null) => {
                      handleFieldChange("address", { zone: newValue });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={addressValidation.show && addressValidation.zone?.error}
                        helperText={
                          addressValidation.show && addressValidation.zone?.error
                            ? addressValidation.zone.helperText
                            : undefined
                        }
                      />
                    )}
                  />
                </Box>
                <Box className="form-section" sx={{ flex: 3 }}>
                  <Box sx={{ display: "flex" }}>
                    <Typography component="span" className="form-label">
                      Area
                    </Typography>
                  </Box>

                  <Autocomplete
                    className="form-field"
                    options={areas}
                    getOptionLabel={(area) => area.name || ""}
                    onChange={(event: any, newValue: AreaBriefResponse | null) => {
                      handleFieldChange("address", { area: newValue?.name || null });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={addressValidation.show && addressValidation.area?.error}
                        helperText={
                          addressValidation.show && addressValidation.area?.error
                            ? addressValidation.area.helperText
                            : undefined
                        }
                      />
                    )}
                  />
                </Box>
              </Box>
              <Box className="form-section" sx={{ flex: "1 1" }}>
                <Typography component="span" className="form-label">
                  Street
                </Typography>
                <TextField
                  className="form-field"
                  value={user?.address?.street}
                  error={addressValidation.show && addressValidation.street?.error}
                  helperText={
                    addressValidation.show && addressValidation.street?.error
                      ? addressValidation.street.helperText
                      : undefined
                  }
                  onChange={(e) => handleFieldChange("address", { street: e.target.value })}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", columnGap: "12px" }}>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Building
                </Typography>
                <TextField
                  className="form-field"
                  value={user?.address?.building}
                  error={addressValidation.show && addressValidation.building?.error}
                  helperText={
                    addressValidation.show && addressValidation.building?.error
                      ? addressValidation.building.helperText
                      : undefined
                  }
                  onChange={(e) => handleFieldChange("address", { building: e.target.value })}
                />
              </Box>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Flat
                </Typography>
                <TextField
                  className="form-field"
                  value={user?.address?.flat}
                  error={addressValidation.show && addressValidation.flat?.error}
                  helperText={
                    addressValidation.show && addressValidation.flat?.error
                      ? addressValidation.flat.helperText
                      : undefined
                  }
                  onChange={(e) => handleFieldChange("address", { flat: e.target.value })}
                />
              </Box>
            </Box>
            <Box className="form-section">
              <Typography component="span" className="form-label">
                Address Detail
              </Typography>
              <TextField
                sx={{
                  "& .MuiInputBase-root > textarea,& .MuiInputBase-root": { height: "auto", minHeight: "56px" },
                }}
                error={addressValidation.show && addressValidation.detail?.error}
                helperText={
                  addressValidation.show && addressValidation.detail?.error
                    ? addressValidation.detail.helperText
                    : undefined
                }
                onChange={(e) => handleFieldChange("address", { detail: e.target.value })}
                className="form-field"
                multiline
                value={user?.address?.detail}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", columnGap: "12px" }}>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Delivery Note
                </Typography>
                <TextField
                  onChange={(e) => handleFieldChange("deliveryNote", e.target.value)}
                  className="form-field"
                  multiline
                  value={user?.deliveryNote}
                />
              </Box>
              {driverList && driverList.length > 0 ? (
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Delivery Driver
                  </Typography>
                  <Select value={user?.address.driverId || null} onChange={driverChange}>
                    {driverList?.map((driverItem) => (
                      <MenuItem key={driverItem.id} value={driverItem.id}>
                        {driverItem.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              ) : null}
            </Box>
            <Box className="form-section">
              <Typography className="form-label-title">Medical Details</Typography>
            </Box>
            <Box className="form-section">
              <Typography component="span" className="form-label">
                Allergens
              </Typography>
              <Box
                className="tagging-container"
                sx={{
                  width: "100%",
                  borderRadius: "8px",
                  border: "1px solid #e5e7ea",
                  padding: "8px",
                }}
              >
                {allergens.map((allergen) => (
                  <Box className="tagging">
                    <Typography>{allergen}</Typography>
                    <CloseIconCircle onClick={() => deleteAllergen(allergen)} />
                  </Box>
                ))}
              </Box>
              <Box width={200} sx={{ marginTop: "4px" }}>
                <Tagging
                  input={allergenList}
                  placeholder="Add Allergen"
                  onChange={(...args) => updateAllergens(...args)}
                />
              </Box>
            </Box>
            <Box className="form-section">
              <Typography component="span" className="form-label">
                Medical Conditions
              </Typography>
              <Box
                className="tagging-container"
                sx={{
                  width: "100%",
                  borderRadius: "8px",
                  border: "1px solid #e5e7ea",
                  padding: "8px",
                }}
              >
                {medicalConditions.map((medicalCondition) => (
                  <Box className="tagging">
                    <Typography>{medicalCondition}</Typography>
                    <CloseIconCircle onClick={() => deleteMedicalCondition(medicalCondition)} />
                  </Box>
                ))}
              </Box>
              <Box width={250} sx={{ marginTop: "4px" }}>
                <Tagging
                  input={medicalConditionList}
                  placeholder="Add Medical Condition"
                  onChange={(...args) => updateMedicalConditions(...args)}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <LoadingButton
            fullWidth
            loading={loading}
            color="secondary"
            variant="contained"
            sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
            onClick={saveChanges}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
